import PouchDB from 'pouchdb'
import { type Base } from '../types/database.types'

function _hasErrors(source: Record<string, unknown>): boolean {
    for (const value of Object.values(source)) {
        if (Array.isArray(value)) {
            if (
                value.length > 0 &&
                (value.every(
                    currentValue => typeof currentValue === 'string',
                ) ||
                    value.some(currentValue => _hasErrors(currentValue)))
            ) {
                return true
            }
        } else if (typeof value === 'object') {
            if (_hasErrors(value as Record<string, unknown>)) {
                return true
            }
        }
    }

    return false
}

export function hasErrors(
    doc: PouchDB.Core.Document<Base> &
        PouchDB.Core.GetMeta & {
            metadata_?: { errors?: Record<string, unknown> }
            metadata?: { errors?: Record<string, unknown> }
        },
): boolean {
    if (doc.metadata_ && doc.metadata_.errors) {
        return _hasErrors(doc.metadata_.errors)
    } else if (doc.metadata && doc.metadata.errors) {
        return _hasErrors(doc.metadata.errors)
    } else {
        return false
    }
}

export function getErrors(
    doc: PouchDB.Core.Document<Base> &
        PouchDB.Core.GetMeta & {
            metadata_?: { errors?: Record<string, unknown> }
            metadata?: { errors?: Record<string, unknown> }
        },
): Record<string, unknown> | undefined {
    const errors =
        doc.metadata_?.errors && _hasErrors(doc.metadata_.errors)
            ? doc.metadata_.errors
            : doc.metadata?.errors && _hasErrors(doc.metadata.errors)
              ? doc.metadata.errors
              : undefined

    if (!errors) return undefined

    const result: Record<string, string> = {}

    for (const [key, value] of Object.entries(errors)) {
        if (typeof value === 'string') {
            result[key] = value
        } else if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value)
        ) {
            // value is { nestedKey: nestedValue }
            for (const [nestedKey, nestedValue] of Object.entries(value)) {
                if (
                    nestedValue &&
                    typeof nestedValue === 'object' &&
                    !Array.isArray(nestedValue)
                ) {
                    // nestedValue is { subKey: arr }
                    for (const [subKey, arr] of Object.entries(nestedValue)) {
                        if (Array.isArray(arr) && arr.length > 0) {
                            result[`${key}.${nestedKey}.${subKey}`] =
                                arr.join(', ')
                        }
                    }
                }
            }
        }
    }

    return Object.keys(result).length > 0 ? result : undefined
}

export type Validator<T> = (input: T) => string | undefined

export function isValid<T>(input: T, validators: Array<Validator<T>>): boolean {
    return validate<T>(input, validators).length === 0
}

export function validate<T>(
    input: T,
    validators: Array<Validator<T>>,
): Array<string> {
    return validators
        .map(validator => {
            return validator(input)
        })
        .filter(result => {
            return result !== undefined
        })
}
